import { useQuery, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { Notification } from '@/lib/types';
import { useAuth } from './use-auth';
import { apiRequest } from '@/lib/queryClient';

export function useNotifications() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  // Determine API endpoint based on user role
  const notificationsEndpoint = isAdmin ? '/api/admin/notifications' : '/api/notifications';
  
  // Fetch notifications
  const { 
    data: notifications = [], 
    isLoading, 
    error 
  } = useQuery<Notification[]>({
    queryKey: [notificationsEndpoint],
    enabled: !!user,
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  // Mark as read mutation (using different endpoints for admin vs regular users)
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: number) => {
      const endpoint = isAdmin 
        ? `/api/admin/notifications/${notificationId}/read`
        : `/api/notifications/${notificationId}/read`;
      return apiRequest("PATCH", endpoint);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [notificationsEndpoint] });
    }
  });
  
  // Delete notification mutation (using different endpoints for admin vs regular users)
  const deleteNotificationMutation = useMutation({
    mutationFn: (notificationId: number) => {
      const endpoint = isAdmin 
        ? `/api/admin/notifications/${notificationId}`
        : `/api/notifications/${notificationId}`;
      return apiRequest("DELETE", endpoint);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [notificationsEndpoint] });
    }
  });
  
  // Handle read notification
  const markAsRead = (notificationId: number) => {
    markAsReadMutation.mutate(notificationId);
  };
  
  // Handle delete notification
  const deleteNotification = (notificationId: number) => {
    deleteNotificationMutation.mutate(notificationId);
  };
  
  // Get unread count 
  const unreadCount = notifications.filter(notification => !notification.read).length;
  
  return {
    notifications,
    isLoading,
    error,
    markAsRead,
    deleteNotification,
    unreadCount
  };
}